// Signed-in identity and what the user may do.
//
// One API call answers all of it: `GET /me` returns the person, their internal
// step roles per workspace, their company approval authority from the
// assignments table, and explicit capability flags. The old client assembled
// this from four sources and parsed role out of team names; none of that comes
// across — the server returns roles.
//
// Two rules worth stating out loud:
//   1. A profile that fails to load FAILS CLOSED. `loadSession` throws and the
//      shell renders an error. The old client failed open (every step lit)
//      because a second gate re-checked server-side; the API is now the only
//      gate, so failing open would be a hole.
//   2. Everything here is an optimistic hint for the UI — it disables a control
//      the API would refuse instead of erroring on click. The API enforces
//      independently and is the authority.
import { getClient } from '../api/client'
import type { Profile, ProfileAssignment } from '../api/types'
import type { CurrentUser, Invoice } from './types'
import {
  ACCOUNTING_STEP_GROUPS,
  ASSIGNMENT_ROLES,
  DecisionStep,
  WORKSPACES,
  roleForToken,
  type Role,
  type Workspace,
} from './constants'

/** The company an approval appointment is scoped to. `id` is what authority
    matches on — the API matches on `company_id` and never on a name, so a
    company rename cannot move authority. `name` is display only. */
export interface CompanyScope {
  id?: string
  name?: string
}

/** One active appointment, as the profile reports it. */
export interface AssignmentInfo {
  id: string
  workspace: string
  role: string
  companyId?: string
  companyName?: string
  /** The catch-all expansion for THIS seat: the member companies a seat on the
      `Other` anchor also reaches. Deciding and seeing only — never appointing. */
  coversCompanyIds: string[]
}

export interface Session {
  user: CurrentUser
  /** Roles in the active workspace (see scopeSession). */
  roles: Role[]
  /** Companies the user is an active director for. LITERAL — this is the
      APPOINTMENT scope, and the Appoint pickers must keep reading it. */
  directorCompanies: CompanyScope[]
  /** Companies the user is an active department head for. LITERAL, as above. */
  departmentCompanies: CompanyScope[]
  /** Companies the user is an active `oversight` seat for. LITERAL, as above
      (issue 2, 2026-08-10 — Oversight Approval is company-scoped now, exactly
      like director and department-head). The list an oversight seat may
      appoint `director`/`department-head` within and retire
      `department-head`/`fleet` within, company-exact. */
  oversightCompanies: CompanyScope[]
  /** AP/AR seats. Company-scoped like the three tiers, but AP/AR is MANDATORY:
      an unstaffed company freezes rather than skipping (issue 3, 2026-08-11). */
  apArCompanies: CompanyScope[]
  /** Every company id a director seat DECIDES for, catch-all expansion included.
      `canDecide` tests a record against this, never against the literal list. */
  directorCoveredCompanyIds: string[]
  /** As above, for department-head seats. */
  departmentCoveredCompanyIds: string[]
  /** As above, for `oversight` seats. `canDecide`'s Oversight arm tests a
      record's `company_id` against this, exactly like the other two tiers —
      there is no longer a bare "does the user hold oversight at all" flag,
      because oversight no longer decides with no company clause. */
  oversightCoveredCompanyIds: string[]
  /** As above, for `ap-ar` seats. `canDecide`'s AP/AR arm tests against this. */
  apArCoveredCompanyIds: string[]
  rolesByWs: Record<Workspace, Role[]>
  directorCompaniesByWs: Record<Workspace, CompanyScope[]>
  departmentCompaniesByWs: Record<Workspace, CompanyScope[]>
  oversightCompaniesByWs: Record<Workspace, CompanyScope[]>
  apArCompaniesByWs: Record<Workspace, CompanyScope[]>
  directorCoveredCompanyIdsByWs: Record<Workspace, string[]>
  departmentCoveredCompanyIdsByWs: Record<Workspace, string[]>
  oversightCoveredCompanyIdsByWs: Record<Workspace, string[]>
  apArCoveredCompanyIdsByWs: Record<Workspace, string[]>
  /** Every active appointment, all workspaces (the Assignments gate reads it). */
  assignments: AssignmentInfo[]
  /** Companies whose records the API will show this user. Copy for UI wording
      only — row visibility is the API's WHERE clause, not this list. */
  visibleCompanyIds: string[]
  /** May the user see source documents? Policy, not convenience: a company
      approver never gets file access, because one upload PDF can hold invoices
      for several companies. */
  canPreviewPdf: boolean
  /** May the caller open the Assignments tab at all — the server's capability
      flag. Renamed from `canManageAssignments`, because appointing is a
      narrower question than viewing (see `visibleAssignmentScreens`, which
      derives WHICH of the tab's screens to show from roles and assignments,
      not from this flag). */
  canViewAssignments: boolean
  canManageReferences: boolean
  canManageFleet: boolean
  isInternal: boolean
}

const emptyByWs = <T,>(value: () => T): Record<Workspace, T> =>
  Object.fromEntries(WORKSPACES.map((ws) => [ws, value()])) as Record<Workspace, T>

/** A session view scoped to one workspace, so screens and `canDecide` work on
    the active workspace without knowing there could be others. */
export function scopeSession(session: Session, workspace: Workspace): Session {
  return {
    ...session,
    roles: session.rolesByWs[workspace] ?? [],
    directorCompanies: session.directorCompaniesByWs[workspace] ?? [],
    departmentCompanies: session.departmentCompaniesByWs[workspace] ?? [],
    oversightCompanies: session.oversightCompaniesByWs[workspace] ?? [],
    apArCompanies: session.apArCompaniesByWs[workspace] ?? [],
    directorCoveredCompanyIds: session.directorCoveredCompanyIdsByWs[workspace] ?? [],
    departmentCoveredCompanyIds: session.departmentCoveredCompanyIdsByWs[workspace] ?? [],
    oversightCoveredCompanyIds: session.oversightCoveredCompanyIdsByWs[workspace] ?? [],
    apArCoveredCompanyIds: session.apArCoveredCompanyIdsByWs[workspace] ?? [],
  }
}

/** Workspaces the user can act in. A user holding only workspace-independent
    roles gets none and lands on their own page instead of an empty queue.
    `Rules` and `Analytics` joined that set on 2026-08-13 and must be listed
    here for the same reason `Admin` and `Training` are: none of the four puts
    a row in anybody's queue, so a holder of only those would otherwise be
    handed an Invoices tab with nothing in it. */
const WORKSPACE_INDEPENDENT_ROLES: readonly Role[] = ['Admin', 'Training', 'Rules', 'Analytics']

export function availableWorkspaces(session: Session): Workspace[] {
  return WORKSPACES.filter((ws) =>
    (session.rolesByWs[ws] ?? []).some((r) => !WORKSPACE_INDEPENDENT_ROLES.includes(r)),
  )
}

/** Does the user hold this role in any workspace? For the workspace-independent
    header tabs (Admin, References). */
export function hasRole(session: Session, role: Role): boolean {
  return WORKSPACES.some((ws) => (session.rolesByWs[ws] ?? []).includes(role))
}

function isWorkspace(value: string): value is Workspace {
  return (WORKSPACES as readonly string[]).includes(value)
}

function toAssignmentInfo(a: ProfileAssignment): AssignmentInfo {
  return {
    id: a.id,
    workspace: a.workspace,
    role: a.role,
    companyId: a.company_id ?? undefined,
    companyName: a.company_name ?? undefined,
    coversCompanyIds: [...(a.covers_company_ids ?? [])],
  }
}

/** One seat's deciding reach: its own company plus, for a seat on the `Other`
    anchor, the member companies the server expanded it to. */
function coveredIdsOf(a: AssignmentInfo): string[] {
  const out: string[] = []
  if (a.companyId) out.push(a.companyId)
  for (const id of a.coversCompanyIds) if (id) out.push(id)
  return out
}

/** Builds the session from the profile payload. Exported so the shell can
    re-derive after a role change without another sign-in. */
export function sessionFromProfile(profile: Profile): Session {
  const user: CurrentUser = {
    fullName: profile.display_name || profile.upn || 'Unknown user',
    email: profile.upn ?? '',
    userId: profile.user_id ?? '',
    directoryEmail: profile.directory_email ?? undefined,
  }

  const rolesByWs = emptyByWs<Role[]>(() => [])
  const directorCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const departmentCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const oversightCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const apArCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const directorCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const departmentCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const oversightCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const apArCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())

  for (const [ws, tokens] of Object.entries(profile.roles_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const token of tokens) {
      const role = roleForToken(token)
      if (role && !rolesByWs[ws].includes(role)) rolesByWs[ws].push(role)
    }
  }

  const assignments = (profile.assignments ?? []).map(toAssignmentInfo)

  // Approval authority is data, not membership: an appointment row IS the
  // permission. A row for a workspace this build does not know about is skipped
  // rather than crashing — historical rows outlive a workspace being retired.
  for (const a of assignments) {
    if (!isWorkspace(a.workspace)) continue
    const ws = a.workspace
    const scope: CompanyScope = { id: a.companyId, name: a.companyName }
    if (a.role === ASSIGNMENT_ROLES.director) {
      if (!rolesByWs[ws].includes('DirectorApproval')) rolesByWs[ws].push('DirectorApproval')
      directorCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) directorCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.departmentHead) {
      if (!rolesByWs[ws].includes('DepartmentApproval')) rolesByWs[ws].push('DepartmentApproval')
      departmentCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) departmentCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.oversight) {
      // Company-scoped since 2026-08-10, exactly like director and
      // department-head above — no longer "organization-wide, no company
      // scope recorded".
      if (!rolesByWs[ws].includes('OversightApproval')) rolesByWs[ws].push('OversightApproval')
      oversightCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) oversightCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.apAr) {
      // AP/AR (issue 3, 2026-08-11). Same shape as the three tiers: the appointment
      // IS the permission, there is no `ap_ar` user_role, and the seat reaches its
      // own company plus — on the `Other` anchor — that anchor's catch-all members.
      // Omitting this arm would leave the AP/AR queue dark and `canDecide` refusing
      // every row, which is the exact failure the oversight arm was added to fix.
      if (!rolesByWs[ws].includes('ApArApproval')) rolesByWs[ws].push('ApArApproval')
      apArCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) apArCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.fleet) {
      // Unlocks the vehicles master and nothing else — not an approval tier.
      if (!rolesByWs[ws].includes('FleetManagement')) rolesByWs[ws].push('FleetManagement')
    }
  }

  // The server sends the expanded lists per workspace as well (§1.1), and they are
  // the authority; the per-seat sets above say the same thing and cover a payload
  // that carries only one of the two. Union, so neither can narrow the other.
  for (const [ws, ids] of Object.entries(profile.director_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) directorCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.department_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) departmentCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.oversight_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) oversightCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.ap_ar_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) apArCoveredByWs[ws].add(id)
  }
  const directorCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const departmentCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const oversightCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const apArCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  for (const ws of WORKSPACES) {
    directorCoveredCompanyIdsByWs[ws] = [...directorCoveredByWs[ws]]
    departmentCoveredCompanyIdsByWs[ws] = [...departmentCoveredByWs[ws]]
    oversightCoveredCompanyIdsByWs[ws] = [...oversightCoveredByWs[ws]]
    apArCoveredCompanyIdsByWs[ws] = [...apArCoveredByWs[ws]]
  }

  const [first] = WORKSPACES
  const capabilities = profile.capabilities
  return {
    user,
    roles: rolesByWs[first] ?? [],
    directorCompanies: directorCompaniesByWs[first] ?? [],
    departmentCompanies: departmentCompaniesByWs[first] ?? [],
    oversightCompanies: oversightCompaniesByWs[first] ?? [],
    apArCompanies: apArCompaniesByWs[first] ?? [],
    directorCoveredCompanyIds: directorCoveredCompanyIdsByWs[first] ?? [],
    departmentCoveredCompanyIds: departmentCoveredCompanyIdsByWs[first] ?? [],
    oversightCoveredCompanyIds: oversightCoveredCompanyIdsByWs[first] ?? [],
    apArCoveredCompanyIds: apArCoveredCompanyIdsByWs[first] ?? [],
    rolesByWs,
    directorCompaniesByWs,
    departmentCompaniesByWs,
    oversightCompaniesByWs,
    apArCompaniesByWs,
    directorCoveredCompanyIdsByWs,
    departmentCoveredCompanyIdsByWs,
    oversightCoveredCompanyIdsByWs,
    apArCoveredCompanyIdsByWs,
    assignments,
    visibleCompanyIds: profile.visible_company_ids ?? [],
    canPreviewPdf: !!capabilities?.can_preview_pdf,
    // `?? can_manage_assignments`: the server's compatibility shim (one
    // release) sends both keys with the same value; a tab left open across a
    // deploy that lands only the new key still reads it. Once the shim drops
    // (docs/questions.md), so does the fallback.
    canViewAssignments: !!(capabilities?.can_view_assignments ?? capabilities?.can_manage_assignments),
    canManageReferences: !!capabilities?.can_manage_references,
    canManageFleet: !!capabilities?.can_manage_fleet,
    isInternal: !!capabilities?.is_internal,
  }
}

/** Loads the profile. Throws on failure — the caller renders an error state
    rather than a usable UI (see the fail-closed rule above). */
export async function loadSession(): Promise<Session> {
  const profile = await getClient().getProfile()
  return sessionFromProfile(profile)
}

/** The server's own capability flag: any role, or any active assignment.
    HALF of the real gate — a true value here can still come with zero
    visible screens (see `visibleAssignmentScreens`'s fail-closed note), so
    this alone must never decide whether the tab renders. Use
    `canOpenAssignments` (below) for that; this export exists for that
    function and for the tests that pin the two halves' relationship. */
export function canViewAssignments(session: Session): boolean {
  return session.canViewAssignments
}

/** One of the Assignments tab's three screens. Order is significant: it is
    the order the tab lists them in. */
export type AssignmentScreen = 'admin' | 'accounting' | 'approval'

/**
 * Which of the Assignments tab's three screens this session may open.
 * `admin` sees all three, and ONLY on literal `hasRole(session,'Admin')` —
 * deliberately not derived from `ADMIN_STEP_GROUPS`, unlike the accounting arm
 * below, because deriving it would let anyone holding one of the OTHER
 * ADMIN_STEP_GROUPS roles (References, Rules, Analytics) onto the Admin
 * screen, and appointing on that screen is admin-only, full stop (owner item
 * 12, 2026-08-13; C15). Otherwise:
 *
 *   - `accounting` shows if the session holds any role in
 *     `ACCOUNTING_STEP_GROUPS` — DERIVED from the constant, never a
 *     hand-written role list, so a sixth group added there is covered here
 *     for free instead of silently rendering for someone this predicate does
 *     not admit. References, Rules and Analytics moved OUT of this constant
 *     into `ADMIN_STEP_GROUPS` (owner item 12), so a References/Rules/
 *     Analytics-only holder no longer matches here either — accepted
 *     consequence, see the C4 invariant test's forward-direction exception.
 *   - `approval` shows if the session holds ANY active assignment in the
 *     workspace, ROLE-AGNOSTIC. Role-agnostic is still required, though no
 *     longer for the original reason (an oversight row carried `company_id`
 *     NULL until 2026-08-10, so a company-keyed test gave an oversight-only
 *     holder a tab with zero screens). It is required now because `fleet` is an
 *     assignment that is not an approval tier: keying on the tier roles would
 *     lose a fleet-only holder the company card their own seat sits on.
 *
 * FAILS CLOSED, NOT SILENTLY. `roleForToken` (used to build `session.roles`,
 * :164-170 below) drops a `user_role` token it does not recognize. If the
 * server ships a tenth `user_role` value ahead of a client that knows it,
 * `canViewAssignments` reads `true` (the session holds SOME role) while this
 * function finds none of them in `ACCOUNTING_STEP_GROUPS`/`ADMIN_STEP_GROUPS`
 * and no assignment either — the tab's capability is true and its screen list
 * is empty, so the tab hides. That is the correct direction to fail in.
 * Unlike before item 12, this is no longer only a theoretical case for a
 * FUTURE unrecognized token: References, Rules and Analytics, held ALONE,
 * construct it TODAY with tokens the client fully recognizes, because their
 * groups now live on the Admin screen and this predicate refuses to open that
 * screen for them. The C4 invariant test names this exception explicitly
 * rather than silently exempting it.
 *
 * NOT THE SAME EXPRESSION AS THE SERVER'S WORKSPACE FILTER, on purpose but
 * worth stating: `canViewAssignments` (`authority.ts`) tests
 * `a.workspace === user.workspace` — the caller's OWN workspace. This function
 * tests `WORKSPACES.some(ws => a.workspace === ws)` — ANY workspace this
 * build knows about — because `Session` at this unscoped layer carries no
 * "current workspace" of its own (`scopeSession` is what narrows to one, and
 * it runs after this). The two agree today only because `WORKSPACES` has
 * exactly one entry, so `canOpenAssignments` below ANDs this function's result
 * with `canViewAssignments(session)` rather than trusting either alone. The
 * fixture loop pins both directions of that agreement as it stands today; it
 * cannot construct the second-workspace case that would split them, because
 * no fixture can appoint into a workspace this build does not know.
 */
export function visibleAssignmentScreens(session: Session): AssignmentScreen[] {
  if (hasRole(session, 'Admin')) return ['admin', 'accounting', 'approval']
  const screens: AssignmentScreen[] = []
  const accountingRoles = ACCOUNTING_STEP_GROUPS.map((g) => g.role)
  if (accountingRoles.some((r) => hasRole(session, r))) screens.push('accounting')
  const hasAnyAssignment = WORKSPACES.some((ws) =>
    session.assignments.some((a) => a.workspace === ws),
  )
  if (hasAnyAssignment) screens.push('approval')
  return screens
}

/**
 * The ONE gate for "does the Assignments tab actually open" — both halves,
 * ANDed: the server's own capability flag AND at least one screen the client
 * can derive. Neither alone is enough: `canViewAssignments` can be true with
 * zero visible screens (this function's own note, and the C4 invariant
 * test), and `visibleAssignmentScreens` never consults the server at all. Use
 * this everywhere the question is "should the tab exist" — the landing page,
 * the nav tab, and the screen's own render guard — rather than letting each
 * site reinvent the AND and risk one of them trusting a single half.
 */
export function canOpenAssignments(session: Session): boolean {
  return canViewAssignments(session) && visibleAssignmentScreens(session).length > 0
}

/**
 * May the caller open the Analytics tab at all — the new `analytics` role
 * (owner item 12, 2026-08-13). Mirrors `canOpenAssignments`: one named
 * predicate, asked once, so the nav tab and the render guard cannot drift
 * into asking the question two different ways.
 *
 * Unlike Assignments, holding the role is the whole answer here — there is no
 * second, server-computed capability flag to AND against, and no per-screen
 * derivation: `analytics` is not cross-company (`isInternal` deliberately
 * excludes it, C14), but the dashboard itself has no company scoping to grant
 * — it aggregates across whatever `visible_company_ids` already lets the
 * caller's OTHER roles/assignments see, same as before this role existed.
 */
export function canOpenAnalytics(session: Session): boolean {
  return hasRole(session, 'Analytics')
}

/** Where `App.tsx` lands a user with no pipeline node lit. Pulled out as a
    pure function so the order is testable without rendering the shell:
    References before Assignments before the dashboard. That order matters
    because the widened Assignments capability (any role, any active
    assignment) now covers a references-only or fleet-only user too, and they
    must land on the screen they actually work, not on Assignments. */
export function landingPage(session: Session): 'training' | 'assignments' | 'dashboard' {
  if (hasRole(session, 'Training') || hasRole(session, 'FleetManagement')) return 'training'
  if (canOpenAssignments(session)) return 'assignments'
  return 'dashboard'
}

/** The document-access policy, straight from the profile's capability flag. */
export function canPreviewPdf(session: Session): boolean {
  return session.canPreviewPdf
}

// ---- Decision rights --------------------------------------------------------
// A hint that mirrors the API's gate (api-contract §6.2): an internal step is
// its role; the three approval tiers are an active appointment that COVERS the
// record's company.
//
// "Covers" is the expanded answer, not the literal one. A seat on the `Other`
// anchor decides for the anchor AND for its member companies, because the
// `… - Other` team owned their records — the server computes that expansion and
// sends it as `*_covered_companies_by_workspace`. Testing the literal seat list
// instead hid the Approve button on a member company's invoice the API would have
// accepted, leaving the record unactionable for the only person who could act.
// APPOINTMENT authority is the other half of the same rule and stays literal (the
// 2026-08-03 broker ruling): the Appoint pickers read `directorCompanies`, which
// is why that list is kept unexpanded rather than replaced.

const STEP_ROLE: Partial<Record<DecisionStep, Role>> = {
  [DecisionStep.ImportControl]: 'ImportControl',
  [DecisionStep.ExportControl]: 'ExportControl',
  [DecisionStep.ManualEntry]: 'ManualEntry',
  [DecisionStep.ClosingConfirmation]: 'ClosingConfirmation',
  [DecisionStep.DenialReview]: 'DenialReview',
}

export function canDecide(session: Session, step: DecisionStep, row: Invoice): boolean {
  // Oversight takes an active `oversight` APPOINTMENT and nothing else. There is
  // no `oversight_approval` user_role (api-contract §0.3): the appointment alone
  // carries the tier's authority and the document access, so requiring a second
  // grant would disable a control the API allows. It does NOT carry cross-company
  // visibility — that ended with the NULL company on 2026-08-10, as the next line
  // says; the two statements contradicted each other until then.
  // COMPANY-SCOPED since 2026-08-10, exactly like Director and Department below —
  // `oversightCoveredCompanyIds` is the scoped, catch-all-expanded field
  // `scopeSession` sets for the active workspace, mirroring the other two tiers
  // exactly. There is no longer a bare "holds oversight anywhere" flag.
  if (step === DecisionStep.Oversight) {
    return coversCompany(session.oversightCoveredCompanyIds, row)
  }
  // AP/AR is company-scoped in exactly the same way. It is MANDATORY rather than
  // skippable, but that is a routing property, not an authority one — the question
  // here is only whether this caller holds an `ap-ar` seat covering the row.
  if (step === DecisionStep.ApAr) {
    return coversCompany(session.apArCoveredCompanyIds, row)
  }
  if (step === DecisionStep.Director) {
    return coversCompany(session.directorCoveredCompanyIds, row)
  }
  if (step === DecisionStep.Department) {
    return coversCompany(session.departmentCoveredCompanyIds, row)
  }
  const role = STEP_ROLE[step]
  return !!role && session.roles.includes(role)
}

/** Company match by id, exactly as the API does it. An empty id covers nothing:
    the API's `company_id = $1` would not match it either. */
function coversCompany(companyIds: readonly string[], row: Invoice): boolean {
  const companyId = row.company_id ?? ''
  return !!companyId && companyIds.includes(companyId)
}
